import {
  Owner,
  Fee,
  MarkPaidSettlement,
  Expense,
  Revenue,
  QuarterlyReport,
  Statement,
  Payment,
  CondoDocument,
  OwnerDashboard,
  Condo,
  DashboardStats,
  Unit,
  UnitStatus,
  UnitFilters,
  Project,
  Violation,
  MaintenanceRequest,
  Amenity,
  AmenityBooking,
  EmailSchedule,
  BoardMember,
  BoardMeeting,
  BoardResolution,
  WorkOrder,
  Inspection,
  PackageItem,
  VisitorAuthorization,
  ParkingSpot,
  StatusCertificateRecord,
  StatusCertificateRequestRecord,
  Vendor,
  Announcement,
  LateFeePolicy,
  AccessKey,
  UnitTransfer,
  BulkFeeRequest,
  OwnerNotification,
  MaintenanceSchedule,
} from '../../types';

// ---------------------------------------------------------------------------
// Sprint 94 (finding 94.4) — request-generation guard.
//
// The store uses a single global `isLoading` flag and last-write-wins state
// updates. When the user switches condos, a slow in-flight fetch for condo A
// can resolve AFTER the switch to condo B and clobber B's freshly-loaded data
// (and incorrectly clear `isLoading` while B's fetch is still running).
//
// Fix: a monotonically increasing token captured when a fetch *starts*. On
// resolve, we compare against the latest token; if a newer fetch has since
// begun, we discard the stale response (no `set`, no `setLoading(false)`).
// The token lives at module scope — intentionally OUTSIDE the Zustand store
// shape — so the store's public API/state is unchanged and consumers don't
// break.
//
// Usage inside a fetch:
//   const token = nextRequestToken();
//   const data = await apiService.getX(condoId);
//   if (isStaleRequest(token)) return;   // a newer fetch superseded us
//   set({ ... });
// and clear `isLoading` only when `!isStaleRequest(token)`.
// Per-resource request-generation guard (Sprint 94, fixed Round 7): a newer
// fetch for the SAME resource supersedes an older one (e.g. after a condo
// switch), but fetches for DIFFERENT resources fired in the same tick must
// NOT cancel each other. The token encodes its resource key so the many
// isStaleRequest(token) call sites need no change.
const __seqByKey: Record<string, number> = {};
export const nextRequestToken = (key: string): string => {
  const seq = (__seqByKey[key] = (__seqByKey[key] ?? 0) + 1);
  return key + ':' + seq;
};
export const isStaleRequest = (token: string): boolean => {
  const i = token.lastIndexOf(':');
  const key = token.slice(0, i);
  return Number(token.slice(i + 1)) !== __seqByKey[key];
};

// ---------------------------------------------------------------------------
// Sprint 106 (106.1) — per-resource loading/error flags.
//
// The store historically exposed a SINGLE global `isLoading` boolean and one
// `error` string shared by ~19 fetch actions: a slow `fetchOwners` kept a
// `fetchFees`-driven page spinning, and a stale condo-A error lingered until the
// next success of ANY resource. We now also track `loadingByKey`/`errorByKey`
// maps keyed by the SAME resource keys the request-token infra uses. The two
// helpers below patch one key and re-derive the legacy globals so existing
// consumers keep working unchanged:
//   isLoading = any key true; error = first non-null key error.
// The keyed flags are written ONLY by the latest (non-stale) request — a
// superseded resolution never flips them (the caller guards with isStaleRequest).
export const deriveGlobals = (
  loadingByKey: Record<string, boolean>,
  errorByKey: Record<string, string | null>,
): { isLoading: boolean; error: string | null } => ({
  isLoading: Object.values(loadingByKey).some(Boolean),
  error: Object.values(errorByKey).find(e => e != null) ?? null,
});

// State-updater builders for the keyed fetch lifecycle (106.1). Each returns a
// zustand `set` updater that patches ONE resource key's loading/error and
// re-derives the legacy globals. `loadStart` clears the key's prior error so a
// stale condo-A error doesn't linger after a condo switch; `loadSettle` writes
// the resolved error (null on success) and clears the key's loading flag.
export type KeyedState = { loadingByKey: Record<string, boolean>; errorByKey: Record<string, string | null> };
export const loadStart = (key: string) => (state: KeyedState) => {
  const loadingByKey = { ...state.loadingByKey, [key]: true };
  const errorByKey = { ...state.errorByKey, [key]: null };
  return { loadingByKey, errorByKey, ...deriveGlobals(loadingByKey, errorByKey) };
};
export const loadSettle = (key: string, error: string | null) => (state: KeyedState) => {
  const loadingByKey = { ...state.loadingByKey, [key]: false };
  const errorByKey = { ...state.errorByKey, [key]: error };
  return { loadingByKey, errorByKey, ...deriveGlobals(loadingByKey, errorByKey) };
};

// Test-only hook so specs can reset the generation counter between cases and
// assert guard behaviour deterministically. Not part of the store's public
// runtime API — consumers must not depend on it.
export const __test__ = {
  resetRequestSeq: () => { for (const k of Object.keys(__seqByKey)) delete __seqByKey[k]; },
  peekRequestSeq: () => Math.max(0, ...Object.values(__seqByKey), 0),
};

// Date utility functions
export const dateUtils = {
  startOfQuarter: (date: Date): Date => {
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), quarter * 3, 1);
  },

  endOfQuarter: (date: Date): Date => {
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), (quarter + 1) * 3, 0, 23, 59, 59, 999);
  },

  isWithinInterval: (date: Date, interval: { start: Date; end: Date }): boolean => {
    return date >= interval.start && date <= interval.end;
  },

  addMonths: (date: Date, months: number): Date => {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
  },

  getCurrentQuarter: (): number => {
    return Math.floor(new Date().getMonth() / 3) + 1;
  },

  getCurrentYear: (): number => {
    return new Date().getFullYear();
  }
};

export interface CondoStore {
  // Data Collections
  condos: Condo[];
  units: Unit[];
  owners: Owner[];
  fees: Fee[];
  expenses: Expense[];
  revenues: Revenue[];
  statements: Statement[];
  payments: Payment[];
  documents: CondoDocument[];
  projects: Project[];
  violations: Violation[];
  maintenanceRequests: MaintenanceRequest[];
  amenities: Amenity[];
  amenityBookings: AmenityBooking[];

  // Loading states
  // Legacy GLOBAL flags — kept for back-compat with the ~19 existing consumers.
  // Sprint 106 (106.1): they are now DERIVED from the per-resource maps on every
  // keyed-fetch settle (isLoading = any key true; error = first non-null key
  // error) so a slow fetch of one resource no longer makes another page spin and
  // a stale condo-A error clears when condo-B loads.
  isLoading: boolean;
  error: string | null;

  // Per-resource loading/error maps (Sprint 106, 106.1). Keyed by the SAME
  // resource keys the request-token infra uses
  // (units/owners/fees/projects/expenses/revenues/statements/payments/documents).
  // A page can read just its own resource's state via the selectors below and is
  // never affected by an unrelated resource's in-flight fetch.
  loadingByKey: Record<string, boolean>;
  errorByKey: Record<string, string | null>;

  // Basic state actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Per-resource selectors (Sprint 106, 106.1).
  isResourceLoading: (key: string) => boolean;
  getResourceError: (key: string) => string | null;

  // Condos Management
  fetchCondos: () => Promise<void>;
  createCondo: (condo: Omit<Condo, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateCondo: (id: string, condo: Partial<Condo>) => Promise<void>;
  deleteCondo: (id: string) => Promise<void>;

  // Units Management
  fetchUnits: (condoId?: string) => Promise<void>;
  createUnit: (unit: Omit<Unit, 'id'>) => Promise<void>;
  updateUnit: (id: string, unit: Partial<Unit>) => void;  // Synchronous update
  updateUnitApi: (id: string, unit: Partial<Unit>) => Promise<void>;  // Async API call
  deleteUnit: (id: string) => void;  // Synchronous delete
  deleteUnitApi: (id: string) => Promise<void>;  // Async API call
  getUnitById: (id: string) => Unit | undefined;
  getUnitsByCondo: (condoId: string) => Unit[];
  getUnitsByStatus: (status: UnitStatus, condoId?: string) => Unit[];
  getAvailableUnits: (condoId?: string) => Unit[];
  getOccupiedUnits: (condoId?: string) => Unit[];
  getMaintenanceUnits: (condoId?: string) => Unit[];
  filterUnits: (filters: UnitFilters, condoId?: string) => Unit[];
  assignOwnerToUnit: (unitId: string, ownerId: string) => Promise<void>;
  removeOwnerFromUnit: (unitId: string) => Promise<void>;
  updateUnitStatus: (unitId: string, status: UnitStatus) => Promise<void>;

  // Dashboard Stats
  getDashboardStats: (condoId?: string) => DashboardStats;
  fetchDashboardStats: (condoId?: string) => Promise<DashboardStats>;

  // Owner Management
  fetchOwners: (condoId?: string) => Promise<void>;
  addOwner: (owner: Owner) => void;
  createOwner: (owner: Omit<Owner, 'id'>) => Promise<void>;
  updateOwner: (id: string, owner: Partial<Owner>) => void;
  updateOwnerApi: (id: string, owner: Partial<Owner>) => Promise<void>;
  deleteOwner: (id: string) => void;
  deleteOwnerApi: (id: string) => Promise<void>;
  getOwnerByUnit: (unitNumber: string, condoId?: string) => Owner | undefined;
  getOwnerById: (id: string) => Owner | undefined;

  // Fee Management
  fetchFees: (condoId?: string) => Promise<void>;
  addFee: (fee: Fee) => void;
  createFee: (fee: Omit<Fee, 'id'>) => Promise<void>;
  updateFee: (id: string, fee: Partial<Fee>) => void;
  updateFeeApi: (id: string, fee: Partial<Fee>) => Promise<void>;
  updateFeeStatus: (id: string, status: Fee['status']) => void;
  deleteFee: (id: string) => void;
  deleteFeeApi: (id: string) => Promise<void>;
  /** Sprint 135 (R12-2) — delete a whole condo-wide assessment by its groupId. */
  deleteFeeGroupApi: (groupId: string) => Promise<void>;
  // Sprint 134 (R12-1) — optional settlement. Omitted → server defaults
  // (method MANUAL, no owner cross-check), which is the owner Pay-Now path.
  markFeeAsPaidApi: (id: string, settlement?: MarkPaidSettlement) => Promise<void>;
  getOwnerFees: (ownerId: string) => Fee[];
  getPendingFees: (condoId?: string) => Fee[];
  getOverdueFees: (condoId?: string) => Fee[];
  getOutstandingTotal: (condoId?: string) => number;

  // Project Management. Sprint 151.1 follow-up (ADR-147) removed the local-only
  // addProject/updateProject/deleteProject mutators — they mutated the store
  // without persisting, which is how the Projects page silently dropped edits.
  fetchProjects: (condoId?: string) => Promise<void>;
  createProject: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateProjectApi: (id: string, project: Partial<Project>) => Promise<void>;
  deleteProjectApi: (id: string) => Promise<void>;
  getProjectById: (id: string) => Project | undefined;
  getProjectsByCondo: (condoId: string) => Project[];
  getProjectsByStatus: (status: Project['status'], condoId?: string) => Project[];
  getProjectsByYear: (year: number, condoId?: string) => Project[];
  getProjectsByCategory: (category: string, condoId?: string) => Project[];
  getProjectSummary: (condoId?: string) => {
    totalProjects: number;
    activeProjects: number;
    completedProjects: number;
    totalBudget: number;
    totalActualCost: number;
    budgetVariance: number;
  };

  // Expense Management
  fetchExpenses: (condoId?: string) => Promise<void>;
  addExpense: (expense: Expense) => void;
  createExpense: (expense: Omit<Expense, 'id'>) => Promise<void>;
  updateExpense: (id: string, expense: Partial<Expense>) => void;
  updateExpenseApi: (id: string, expense: Partial<Expense>) => Promise<void>;
  deleteExpense: (id: string) => void;
  deleteExpenseApi: (id: string) => Promise<void>;
  getExpensesByPeriod: (startDate: Date, endDate: Date, condoId?: string) => Expense[];

  // Revenue Management
  fetchRevenues: (condoId?: string) => Promise<void>;
  addRevenue: (revenue: Revenue) => void;
  createRevenue: (revenue: Omit<Revenue, 'id'>) => Promise<void>;
  updateRevenue: (id: string, revenue: Partial<Revenue>) => void;
  deleteRevenue: (id: string) => void;
  getRevenuesByPeriod: (startDate: Date, endDate: Date, condoId?: string) => Revenue[];

  // Statement Management
  fetchStatements: (condoId?: string) => Promise<void>;
  // Sprint 140 (R13-1, ADR-135) — `generateStatements` removed. Statements are
  // derived server-side from fees (no statements table), so there is nothing for
  // a client generator to create; `fetchStatements` is the whole story.
  sendStatementEmail: (statementId: string) => Promise<void>;
  /** Sprint 146 — `period: null` means "every unsent statement in this condo",
   *  which is what the new default "All" filter mode needs. */
  sendBulkStatements: (period: string | null, condoId?: string) => Promise<{ statementId: string; success: boolean; error?: string }[]>;
  getOwnerStatements: (ownerId: string) => Statement[];
  getStatementById: (id: string) => Statement | undefined;

  // Payment Processing
  fetchPayments: (condoId?: string) => Promise<void>;
  // Sprint 108 (ADR-101) — the client-side `processPayment` mock is removed.
  // Real settlement goes through markFeeAsPaidApi (demo) or the Stripe form;
  // the in-memory `payments` slice only ever holds server rows from fetchPayments.
  createPayment: (payment: Omit<Payment, 'id'>) => Promise<void>;
  getOwnerPayments: (ownerId: string) => Payment[];
  getPaymentById: (id: string) => Payment | undefined;
  getRecentPayments: (limit?: number, condoId?: string) => Payment[];
  getPaymentsByUnit: (unitId: string) => Payment[];

  // Document Management
  fetchDocuments: (condoId?: string) => Promise<void>;
  uploadDocument: (document: Omit<CondoDocument, 'id' | 'uploadDate'>) => void;
  uploadDocumentApi: (formData: FormData) => Promise<void>;
  createDocumentApi: (document: Omit<CondoDocument, 'id'>) => Promise<void>;
  updateDocument: (id: string, document: Partial<CondoDocument>) => void;
  deleteDocument: (id: string) => void;
  deleteDocumentApi: (id: string) => Promise<void>;
  getOwnerDocuments: (ownerId: string) => CondoDocument[];
  getDocumentsByCategory: (category: CondoDocument['category'], condoId?: string) => CondoDocument[];
  getPublicDocuments: (condoId?: string) => CondoDocument[];
  searchDocuments: (query: string, condoId?: string) => CondoDocument[];

  // Financial Reporting
  getQuarterlyReport: (quarter: number, year: number, condoId?: string) => QuarterlyReport;
  getCurrentQuarterReport: (condoId?: string) => QuarterlyReport;
  getYearToDateReport: (condoId?: string) => { revenue: number; expenses: number; net: number };
  getMonthlyReport: (month: number, year: number, condoId?: string) => { revenue: number; expenses: number; net: number };

  // Owner Portal
  // Sprint 36 — finding H1/H3. The optional condoId narrows the
  // owner-visible documents to the building they actually live in.
  // (Callers that don't pass one fall back to legacy "uploadedBy"
  // matching for backward compatibility.)
  getOwnerDashboard: (ownerId: string, condoId?: string) => OwnerDashboard;
  getOwnerBalance: (ownerId: string) => number;

  // Unit Statistics
  getUnitStats: (condoId?: string) => {
    totalUnits: number;
    occupiedUnits: number;
    vacantUnits: number;
    maintenanceUnits: number;
    occupancyRate: number;
    unitsByFloor: { floor: number; count: number; occupied: number }[];
    unitsByBedrooms: { bedrooms: number; count: number; occupied: number }[];
  };

  // Violations
  fetchViolations: (condoId?: string) => Promise<void>;
  createViolation: (violation: Omit<Violation, 'id'>) => Promise<void>;
  updateViolation: (id: string, violation: Partial<Violation>) => Promise<void>;
  deleteViolation: (id: string) => Promise<void>;
  sendViolationNotice: (id: string) => Promise<void>;
  resolveViolation: (id: string) => Promise<void>;
  addViolationNote: (id: string, note: string) => Promise<void>;

  // Maintenance Requests
  fetchMaintenanceRequests: (condoId?: string) => Promise<void>;
  // Sprint 116 (116.4) — the backend assigns both `id` and `requestNumber`
  // (createMaintenanceRequest normalizes/synthesizes the latter when absent), so
  // callers never supply either. Typing them out lets the owner-home / MyHomeHub
  // call sites drop their `useCondoStore() as any` cast.
  createMaintenanceRequest: (
    req: Omit<MaintenanceRequest, 'id' | 'requestNumber'>,
  ) => Promise<void>;
  updateMaintenanceRequest: (id: string, req: Partial<MaintenanceRequest>) => Promise<void>;
  deleteMaintenanceRequest: (id: string) => Promise<void>;
  updateMaintenanceRequestStatus: (id: string, status: string) => Promise<void>;

  // Amenities
  fetchAmenities: (condoId?: string) => Promise<void>;
  createAmenity: (amenity: Omit<Amenity, 'id'>) => Promise<void>;
  updateAmenity: (id: string, amenity: Partial<Amenity>) => Promise<void>;
  deleteAmenity: (id: string) => Promise<void>;

  // Amenity Bookings
  fetchAmenityBookings: (condoId?: string) => Promise<void>;
  createAmenityBooking: (booking: Omit<AmenityBooking, 'id'>) => Promise<void>;
  updateAmenityBooking: (id: string, booking: Partial<AmenityBooking>) => Promise<void>;
  deleteAmenityBooking: (id: string) => Promise<void>;
  approveAmenityBooking: (id: string) => Promise<void>;
  cancelAmenityBooking: (id: string) => Promise<void>;

  // Board Management
  boardMembers: BoardMember[];
  boardMeetings: BoardMeeting[];
  boardResolutions: BoardResolution[];
  fetchBoardMembers: (condoId?: string) => Promise<void>;
  createBoardMember: (member: Omit<BoardMember, 'id'>) => Promise<void>;
  updateBoardMember: (id: string, member: Partial<BoardMember>) => Promise<void>;
  deleteBoardMember: (id: string) => Promise<void>;
  fetchBoardMeetings: (condoId?: string) => Promise<void>;
  createBoardMeeting: (meeting: Omit<BoardMeeting, 'id'>) => Promise<void>;
  updateBoardMeeting: (id: string, meeting: Partial<BoardMeeting>) => Promise<void>;
  deleteBoardMeeting: (id: string) => Promise<void>;
  fetchBoardResolutions: (condoId?: string) => Promise<void>;
  createBoardResolution: (res: Omit<BoardResolution, 'id'>) => Promise<void>;
  updateBoardResolution: (id: string, res: Partial<BoardResolution>) => Promise<void>;
  closeBoardResolution: (id: string) => Promise<void>;
  deleteBoardResolution: (id: string) => Promise<void>;

  // Work Orders
  workOrders: WorkOrder[];
  fetchWorkOrders: (condoId?: string) => Promise<void>;
  createWorkOrder: (wo: Omit<WorkOrder, 'id'>) => Promise<void>;
  updateWorkOrder: (id: string, wo: Partial<WorkOrder>) => Promise<void>;
  completeWorkOrder: (id: string, actualCost?: number) => Promise<void>;
  deleteWorkOrder: (id: string) => Promise<void>;

  // Inspections
  inspections: Inspection[];
  fetchInspections: (condoId?: string) => Promise<void>;
  createInspection: (i: Omit<Inspection, 'id'>) => Promise<void>;
  updateInspection: (id: string, i: Partial<Inspection>) => Promise<void>;
  completeInspection: (id: string, result: string) => Promise<void>;
  addInspectionNote: (id: string, note: string) => Promise<void>;
  deleteInspection: (id: string) => Promise<void>;

  // Visitor Authorizations
  visitorAuthorizations: VisitorAuthorization[];
  fetchVisitorAuthorizations: (condoId?: string, unitNumber?: string) => Promise<void>;
  createVisitorAuthorization: (va: Omit<VisitorAuthorization, 'id' | 'createdAt'>) => Promise<void>;
  updateVisitorAuthorization: (id: string, va: Partial<VisitorAuthorization>) => Promise<void>;
  revokeVisitorAuthorization: (id: string) => Promise<void>;
  deleteVisitorAuthorization: (id: string) => Promise<void>;

  // Parking Spots
  parkingSpots: ParkingSpot[];
  fetchParkingSpots: (condoId?: string) => Promise<void>;
  createParkingSpot: (spot: Omit<ParkingSpot, 'id'>) => Promise<void>;
  updateParkingSpot: (id: string, spot: Partial<ParkingSpot>) => Promise<void>;
  assignParkingSpot: (id: string, ownerId: string, ownerName?: string, unitNumber?: string) => Promise<void>;
  unassignParkingSpot: (id: string) => Promise<void>;
  deleteParkingSpot: (id: string) => Promise<void>;

  // Status Certificates
  statusCertificates: StatusCertificateRecord[];
  fetchStatusCertificates: (condoId?: string) => Promise<void>;
  /**
   * Sprint 130 — server-side ledger prefill for the wizard. Given the condo + the
   * owner's login-user id, returns the authoritative outstanding balance, monthly
   * fee, next-due, default flag, open-fee breakdown, and audit snapshot. Resolves
   * to `{ autoFilled: false, resolutionNote }` for an un-activated/unresolved owner.
   */
  prefillStatusCertificate: (condoId: string, ownerId: string) => Promise<any>;
  persistStatusCertificate: (cert: Omit<StatusCertificateRecord, 'id' | 'createdAt'>) => Promise<void>;
  /**
   * Sprint 129 — authoritative issuance: render + persist server-side, return the
   * created record (server-assigned number + id). Throws on failure so the caller
   * blocks download/email rather than delivering an unrecorded certificate.
   */
  issueStatusCertificate: (condoId: string, content: any, requestId?: string) => Promise<StatusCertificateRecord>;
  deleteStatusCertificate: (id: string) => Promise<void>;

  // Status Certificate Requests
  statusCertificateRequests: StatusCertificateRequestRecord[];
  fetchStatusCertificateRequests: (condoId?: string) => Promise<void>;
  createStatusCertificateRequest: (condoId: string, body: any) => Promise<any>;
  cancelStatusCertificateRequest: (id: string, reason?: string) => Promise<void>;

  // Packages
  packages: PackageItem[];
  fetchPackages: (condoId?: string) => Promise<void>;
  createPackage: (p: Omit<PackageItem, 'id'>) => Promise<void>;
  updatePackage: (id: string, p: Partial<PackageItem>) => Promise<void>;
  notifyPackageResident: (id: string) => Promise<void>;
  markPackagePickedUp: (id: string) => Promise<void>;
  deletePackage: (id: string) => Promise<void>;

  // Email Scheduling
  emailSchedules: EmailSchedule[];
  fetchEmailSchedules: (condoId: string) => Promise<void>;
  createEmailSchedule: (schedule: EmailSchedule) => Promise<EmailSchedule>;
  updateEmailSchedule: (id: string, schedule: Partial<EmailSchedule>) => Promise<void>;
  cancelEmailSchedule: (id: string) => Promise<void>;
  deleteEmailSchedule: (id: string) => Promise<void>;
  sendEmailNow: (id: string) => Promise<EmailSchedule>;

  // Vendors (S7.1)
  vendors: Vendor[];
  fetchVendors: (condoId?: string) => Promise<void>;
  createVendor: (v: Omit<Vendor, 'id' | 'createdAt'>) => Promise<void>;
  updateVendor: (id: string, v: Partial<Vendor>) => Promise<void>;
  deleteVendor: (id: string) => Promise<void>;
  rateVendor: (id: string, rating: number) => Promise<void>;

  // Announcements (S7.2)
  announcements: Announcement[];
  fetchAnnouncements: (condoId?: string, publishedOnly?: boolean) => Promise<void>;
  createAnnouncement: (a: Omit<Announcement, 'id' | 'createdAt'>) => Promise<void>;
  updateAnnouncement: (id: string, a: Partial<Announcement>) => Promise<void>;
  archiveAnnouncement: (id: string) => Promise<void>;
  deleteAnnouncement: (id: string) => Promise<void>;

  // Late Fee Policy (S7.3)
  lateFeePolicy: LateFeePolicy | null;
  fetchLateFeePolicy: (condoId: string) => Promise<void>;
  upsertLateFeePolicy: (condoId: string, policy: Omit<LateFeePolicy, 'id' | 'condoId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  deleteLateFeePolicy: (condoId: string) => Promise<void>;

  // Bulk Fees (S8.1)
  bulkCreateFees: (req: BulkFeeRequest) => Promise<void>;

  // Unit Transfers (S8.2)
  unitTransfers: UnitTransfer[];
  fetchUnitTransfers: (condoId?: string) => Promise<void>;
  recordUnitTransfer: (transfer: Omit<UnitTransfer, 'id' | 'createdAt'>) => Promise<UnitTransfer>;

  // Access Keys (S8.3)
  accessKeys: AccessKey[];
  fetchAccessKeys: (condoId?: string) => Promise<void>;
  createAccessKey: (key: Omit<AccessKey, 'id' | 'createdAt'>) => Promise<void>;
  updateAccessKey: (id: string, key: Partial<AccessKey>) => Promise<void>;
  returnAccessKey: (id: string) => Promise<void>;
  reportAccessKeyLost: (id: string) => Promise<void>;
  deleteAccessKey: (id: string) => Promise<void>;

  // Owner Notifications (S9.1)
  notifications: OwnerNotification[];
  fetchNotifications: (condoId: string, activeOnly?: boolean) => Promise<void>;
  createNotification: (n: Omit<OwnerNotification, 'id' | 'createdAt' | 'active'>) => Promise<void>;
  updateNotification: (id: string, n: Partial<OwnerNotification>) => Promise<void>;
  deactivateNotification: (id: string) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;

  // Maintenance Schedules (S9.2)
  maintenanceSchedules: MaintenanceSchedule[];
  fetchMaintenanceSchedules: (condoId: string) => Promise<void>;
  createMaintenanceSchedule: (ms: Omit<MaintenanceSchedule, 'id' | 'createdAt'>) => Promise<void>;
  updateMaintenanceSchedule: (id: string, ms: Partial<MaintenanceSchedule>) => Promise<void>;
  completeMaintenanceSchedule: (id: string) => Promise<void>;
  setMaintenanceScheduleStatus: (id: string, status: string) => Promise<void>;
  deleteMaintenanceSchedule: (id: string) => Promise<void>;

  // Violation Fine Management (S9.3)
  applyViolationFine: (id: string, amount: number) => Promise<void>;
  markViolationFinePaid: (id: string) => Promise<void>;

  // Work Order Vendor/Cost (S9.4)
  assignWorkOrderVendor: (id: string, vendorId: string, vendorName: string, vendorContact?: string) => Promise<void>;
  signOffWorkOrder: (id: string, signedOffBy: string) => Promise<void>;
  updateWorkOrderCosts: (id: string, laborCost: number, materialCost: number, invoiceNumber?: string) => Promise<void>;

  // Utility Methods
  initializeSampleData: (condoId?: string) => void;
  clearAllData: () => void;
}
